/**
 * Content hub landing page + the Content vertical's route map.
 *
 * REQUIRED INTEGRATION (apps/web/src/app.tsx is a shared file — cannot be edited
 * from this vertical). Replace the single `content` Placeholder route with this
 * nested tree so all six screens mount:
 *
 *   import { ContentHub } from './routes/content'
 *   import { PagesAdmin } from './routes/content/pages'
 *   import { PageEditor } from './routes/content/page-editor'
 *   import { SermonsAdmin } from './routes/content/sermons'
 *   import { SermonEditor } from './routes/content/sermon-editor'
 *   import { ContentPublicView } from './routes/content/public-view'
 *
 *   <Route path="content">
 *     <Route index element={<ContentHub />} />
 *     <Route path="pages" element={<PagesAdmin />} />
 *     <Route path="pages/:id" element={<PageEditor />} />     // ":id" === 'new' creates
 *     <Route path="sermons" element={<SermonsAdmin />} />
 *     <Route path="sermons/:id" element={<SermonEditor />} /> // ":id" === 'new' creates
 *     <Route path="preview" element={<ContentPublicView />} />
 *   </Route>
 */
import { Card } from '@fc/ui'
import { ExternalLink, FileText, Mic } from 'lucide-react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'

const tiles = [
  {
    to: '/app/content/pages',
    title: 'Pages',
    description: 'Public website pages with a draft / publish workflow.',
    icon: FileText,
  },
  {
    to: '/app/content/sermons',
    title: 'Sermons',
    description: 'Sermon library with audio / video and optional series.',
    icon: Mic,
  },
  {
    to: '/app/content/preview',
    title: 'Public preview',
    description: 'See how published content renders for visitors.',
    icon: ExternalLink,
  },
]

export function ContentHub() {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Content"
        description="Manage public pages and sermons for your church site."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {tiles.map((tile) => (
          <Link key={tile.to} to={tile.to} className="group focus-visible:outline-none">
            <Card className="h-full p-5 transition-colors hover:bg-card-hover group-focus-visible:ring-[3px] group-focus-visible:ring-ring">
              <div className="mb-3 grid size-10 place-items-center rounded-lg bg-card text-secondary">
                <tile.icon className="size-5" />
              </div>
              <h2 className="font-display text-lg font-semibold">{tile.title}</h2>
              <p className="mt-1 text-sm text-muted">{tile.description}</p>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  )
}
